import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiUser } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getErrorDetail } from '../utils/errorDetail';

const LoginContainer = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  margin-top: 3rem;

  @media (min-width: 768px) {
    margin-top: 4rem;
  }
`;

const LoginCard = styled.div`
  background: var(--white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
`;

const Title = styled.h1`
  text-align: center;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
`;

const Subtitle = styled.p`
  text-align: center;
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--primary-color);
    font-size: 1rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--gray-medium);
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
  }

  &:invalid {
    border-color: var(--danger-color, #f44336);
  }
`;

const Button = styled.button`
  padding: 0.875rem;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const Divider = styled.div`
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
  color: var(--gray-dark);
  font-size: 0.9rem;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: calc(50% - 2rem);
    height: 1px;
    background-color: var(--gray-medium);
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  color: var(--gray-dark);
  font-size: 0.95rem;

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const sessionExpired = (location.state as { sessionExpired?: boolean } | null)?.sessionExpired === true;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await login(formData.email, formData.password);
      setSuccess(t('login.successMessage'));
      // Only honour the one internal return path CheckoutWithStripe sets
      // after a session-expired logout; anything else goes home.
      const returnTo = (location.state as { from?: string } | null)?.from === '/checkout' ? '/checkout' : '/';
      setTimeout(() => {
        navigate(returnTo);
      }, 1500);
    } catch (err: any) {
      setError(getErrorDetail(err) || t('login.defaultError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>{t('login.title')}</Title>
        <Subtitle>{t('login.subtitle')}</Subtitle>

        <Form onSubmit={handleSubmit}>
          {sessionExpired && !error && !success && (
            <ErrorMessage role="alert">{t('checkoutStripe.errorSessionExpired')}</ErrorMessage>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormGroup>
            <Label>
              <span>📧</span>
              {t('login.emailLabel')}
            </Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('login.emailPlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <span>🔒</span>
              {t('login.passwordLabel')}
            </Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('login.passwordPlaceholder')}
            />
          </FormGroup>

          <Button type="submit" disabled={loading}>
            {loading ? t('login.signingInButton') : t('login.signInButton')}
          </Button>
        </Form>

        <Divider>{t('login.orDivider')}</Divider>

        <Button
          type="button"
          onClick={() => navigate('/checkout')}
          style={{ backgroundColor: 'var(--gray-dark)' }}
        >
          <FiUser style={{ marginRight: '0.5rem', display: 'inline' }} />
          {t('login.continueAsGuest')}
        </Button>

        <LinkText>
          {t('login.noAccountPrompt')} <Link to="/register">{t('login.registerLink')}</Link>
        </LinkText>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginPage;